import React, { useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import TrueImpactText, {
  textSelectionNone,
} from 'shared/design-system/base/TrueImpactText';
import {
  FeedbackCommentProps,
  FeedbackSectionId,
  RenderQuestionProps,
} from '../utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import FeedbackCommentTextarea from '../FeedbackCommentTextarea';
import FeedbackComment from '../FeedbackComment';
import { deleteQuestionLevelComment, updateQuestionLevelComment } from '../api';
import { DisplayCommentCount } from '../FeedbackQuestionTitleRender';
import CommentArea from 'npo/pages/NPOFeedback/CommentArea';
import { feedbackSectionIdToString } from 'npo/pages/NPOFeedback/utils';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';

interface ImpactDetailsQuestionProps {
  title: string;
  reportId: number;
  questionId: number;
  sectionId: FeedbackSectionId;
  description: React.ReactNode;
  extraDetails: React.ReactNode;
  details: string;
  bottomComponent?: React.ReactNode;
  currentUserId: RenderQuestionProps['currentUserId'];
  currentUserEmail: RenderQuestionProps['currentUserEmail'];
  currentUserName: RenderQuestionProps['currentUserName'];
  currentUserType: RenderQuestionProps['currentUserType'];
  comments: FeedbackCommentProps[];
  addOrUpdateComment: (
    comment: FeedbackCommentProps,
    index: number | undefined,
  ) => unknown;
  onClick?: () => unknown;
  deleteComment: (index: number) => unknown;
}

const StyledDiv = styled.div<{ open: boolean }>`
  .stage-question-wrapper {
    display: flex;
    gap: ${SPACERS.small};
    align-items: center;
    .stage-question-left-side {
      flex: 1;
      .stage-question {
        ${textSelectionNone}
        padding: ${SPACERS.small};
        margin-bottom: ${SPACERS.tiny};
        border-radius: ${SPACERS.extraTiny};
        border: 1px solid ${COLORS.grayLight};
        transition: background-color 0.5s;
        background-color: ${({ open }) =>
          open ? COLORS.grayLight : COLORS.grayLightest};
        &:hover {
          ${({ open }) =>
            open ? '' : `background-color: ${COLORS.whiteLight};`}
        }
        .stage-question-details {
          cursor: pointer;
          .stage-question-row {
            display: flex;
            gap: ${SPACERS.medium};
          }
        }
        .stage-question-title {
          max-width: 112px;
        }
        .stage-answer {
          flex: 1;
        }
      }
    }
    .stage-comments-count {
      width: 75px;
      cursor: pointer;
    }
  }
  .comments-section {
    .comments {
      margin-top: ${SPACERS.medium};
    }
    .comment-text {
    }
  }
`;

const ImpactDetailsQuestion: React.FC<ImpactDetailsQuestionProps> = ({
  title,
  description,
  comments,
  details,
  extraDetails,
  currentUserEmail,
  currentUserId,
  currentUserName,
  currentUserType,
  sectionId,
  questionId,
  reportId,
  bottomComponent,
  deleteComment,
  addOrUpdateComment,
  onClick,
}) => {
  const [open, setOpen] = useState<boolean>(comments.length > 0);

  const handleComment = useCallback(
    (questionId: number, comment: FeedbackCommentProps, index?: number) => {
      addOrUpdateComment(comment, index);
    },
    [addOrUpdateComment],
  );

  const handleUpdateComment = useCallback(
    (comment: FeedbackCommentProps, index?: number) => {
      addOrUpdateComment(comment, index);
    },
    [addOrUpdateComment],
  );

  const handleClick = useCallback(() => {
    onClick?.();
    setOpen(o => !o);
  }, [setOpen, onClick]);

  const selectedSection = useMemo(
    () => ({
      question: {
        questionId: questionId,
        questionText: '',
      },
      section: {
        label: feedbackSectionIdToString(sectionId),
        sectionId: sectionId,
      },
    }),
    [questionId, sectionId],
  );

  return (
    <StyledDiv open={open}>
      <div className="stage-question-wrapper">
        <div className="stage-question-left-side">
          <div className="stage-question">
            <div className="stage-question-details" onClick={handleClick}>
              <div className="stage-question-row">
                <TrueImpactText
                  variant="body-bold"
                  className="stage-question-title"
                >
                  {title}
                </TrueImpactText>
                <TrueImpactText className="stage-answer">
                  {description}
                </TrueImpactText>
              </div>
              {extraDetails}
            </div>
            {open && (
              <div className="comments-section">
                <div className="comments">
                  {comments.map((c, i) => (
                    <FeedbackComment
                      key={c.commentId || i}
                      comment={c}
                      currentUserName={currentUserName}
                      currentUserType={currentUserType}
                      index={i}
                      reportId={reportId}
                      sectionId={sectionId}
                      currentUserEmail={currentUserEmail}
                      currentUserId={currentUserId}
                      handleSave={updateQuestionLevelComment}
                      handleDelete={deleteQuestionLevelComment}
                      onSuccessSave={handleUpdateComment}
                      onSuccessDelete={deleteComment}
                    />
                  ))}
                </div>
                {currentUserType === 'ti staff' ? (
                  <FeedbackCommentTextarea
                    reportId={reportId}
                    sectionId={sectionId}
                    userName={currentUserName}
                    userType={currentUserType}
                    details={details}
                    onSaveSuccess={handleComment}
                    currentUserEmail={currentUserEmail}
                    currentUserId={currentUserId}
                    questionId={questionId}
                    className="comment-text"
                  />
                ) : (
                  <CommentArea
                    buttonText="Leave Additional Feedback"
                    defaultOpenState={true}
                    currentUserEmail={currentUserEmail}
                    currentUserId={currentUserId}
                    reportId={reportId}
                    details={details}
                    selectedSection={selectedSection}
                    userName={currentUserName}
                    userType={currentUserType}
                    onSuccess={(_, c) => handleComment(questionId, c)}
                  />
                )}
              </div>
            )}
          </div>
          {bottomComponent}
        </div>
        <DisplayCommentCount
          total={comments.length}
          onClick={handleClick}
          className="stage-comments-count"
        />
      </div>
    </StyledDiv>
  );
};

export default ImpactDetailsQuestion;
